/**
 * 圆弧选择状态 Store。
 * 统一管理圆弧选中状态与圆弧变化事件。
 */

import { defineStore } from 'pinia';
import type { SketchArcObject } from '@puppycad/cad-core';
import { EntityInteractionEventType, isSketchArcObject } from '@puppycad/cad-core';
import { BehaviorSubject, type Observable, type Subscription } from 'rxjs';
import { useViewer } from '../useViewer';
import {
  observeViewerEntityInteraction,
  resolveViewerSelectedObject,
} from './viewer-selection-helpers';

//------ 圆弧选择状态 开始------
/**
 * 获取当前主选中的草图圆弧对象，并观测圆弧变化事件。
 */
export const useGetSelectArc = defineStore('get-select-arc', () => {
  let selectedArc: SketchArcObject | null = null;
  let subscriptions: Subscription[] = [];
  let hasSubscribed = false;
  const arcChanged$ = new BehaviorSubject<SketchArcObject | null>(null);
  const { getViewer } = useViewer();

  

  /**
   * 从实体选择管理器同步当前选中圆弧。
   */
  function syncFromManager(): void {
    selectedArc = resolveViewerSelectedObject(getViewer(), isSketchArcObject);
    arcChanged$.next(selectedArc);
  }

  /**
   * 处理圆弧选中事件。
   */
  function handleArcSelected(): void {
    syncFromManager();
  }

  /**
   * 处理选择清空事件。
   */
  function handleSelectionCleared(): void {
    selectedArc = null;
    arcChanged$.next(null);
  }

  /**
   * 显式开始监听圆弧选择事件。
   */
  function subscribe(): void {
    const viewer = getViewer();
    const arcSelected$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.ArcSelected);
    const selectionCleared$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionCleared);
    if (!arcSelected$ || !selectionCleared$ || hasSubscribed) {
      return;
    }
    syncFromManager();
    subscriptions = [
      arcSelected$.subscribe(handleArcSelected),
      selectionCleared$.subscribe(handleSelectionCleared),
    ];
    hasSubscribed = true;
  }

  /**
   * 取消圆弧选择事件监听。
   */
  function unsubscribe(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    hasSubscribed = false;
  }

  return {
    getSelected: (): SketchArcObject | null => selectedArc,
    onChanged: (): Observable<SketchArcObject | null> => arcChanged$.asObservable(),
    subscribe,
    unsubscribe,
  };
});
//------ 圆弧选择状态 结束------
